// A device's declared links (#297, ZENITH.md §8.1 Link, §11.3, ADR-0045
// decisions 1, 2 and 5): the ones that touch this device, each with its
// other end and the word it carries (ADR-0090). **This tab reads** (#875,
// ADR-0149 decision 1): the device's page is Operate's, and Withdraw moved
// with the gesture to Design › Wired's topology, where a pressed line is
// read, re-worded and withdrawn. **The gesture that declares one is on that
// drawing** (#588), where two ports are
// pressed and a popup takes the word — this tab used to hold a picker that
// drew the far device's faceplate a second time, and it went with the form
// on the Plan page and the table on the topology. What stays exported from
// here is what the drawing's popup presses on: `EndTarget`, `endRequest`,
// `takenAt` and `EndFace`, the far end's front from what `/v1` says its
// ports are.
//
// **A link is intent for people.** It configures nothing, it is read by
// nothing that generates, gates, applies or lays out, and withdrawing one
// tells no device anything (decision 2). Both devices' pages list it. What a switch *observes* on the same port is the
// live layer on the faceplate above; the site's topology (`Topology.tsx`)
// compares the two and never merges them, and nothing here turns a
// neighbour into a link.
//
// **The port pressed on the faceplate at the head** (#589) is what this tab
// says the switch observes, in words (#514): the neighbour the switch
// reports on it — the site's device as a link to its page, or an unknown
// device named by what the switch reports of it (ADR-0045 decision 4). The read's own
// line — when the switch wrote the report, `Read again`, or why there is no
// answer — is under the face at the head, on every tab. It is the page's
// one read; nothing here reads a second time.
//
// **A planned device is an end** (#520, ADR-0045 decision 1, ADR-0068): its
// front is drawn from its catalogue model — the only vocabulary it has —
// and the write names the planned row (`planned_device_id`) rather than a
// serial it does not have. A planned device an installer has already bound
// is reached by its serial, as any device of the site is.
import { useEffect, type ReactNode } from "react";
import { api } from "../../api/client";
import { SKIP, useDrained, useQuery } from "../../api/hooks";
import { plural } from "../../dashboard/format";
import { ScopeLink } from "../../shell/scope";
import { Card, Info, Loading, ProblemBox, When } from "../../ui";
import type { Device } from "./commands";
import { equipmentLabel, equipmentWords, namer } from "./pieces";
import DeviceFace, { byNumber, type FacePort } from "./DeviceFace";
import { useDeviceModel, type DeviceModel, type useLiveState } from "./DeviceDetail";
import { documentKeyedRead } from "./keyedBy";
import { LinkCell, NeighbourCell, facePorts, photoOf, reasonWords } from "./Ports";
import { boundName, endWords, otherEnd, otherEndOfPlanned, type Link } from "./verdicts";
import type { components } from "../../api/schema";
import { DeviceRef } from "../../shell/EntityRef";

export type PlannedDevice = components["schemas"]["PlannedDevice"];
type ReportedPort = components["schemas"]["ReportedPort"];

/** One end a link can be declared to: a device of the site, or a planned
 * device of its plan that is still waiting for its hardware. */
export type EndTarget = { kind: "claimed"; device: Device } | { kind: "planned"; planned: PlannedDevice };

/** The target's name, for the button and the sentence: its friendly name
 * (#1906, `pieces.tsx`), through the page's `namer` where it has one so a
 * claimed device bound to a planned one reads by the plan's name. */
export const targetName = (t: EndTarget, name?: (serial: string) => string) => t.kind === "claimed"
  ? (name ? name(t.device.serial) : equipmentWords(equipmentLabel(t.device, undefined)))
  : equipmentWords(equipmentLabel(undefined, t.planned));

/** The request body's end for a target and a port. */
export function endRequest(t: EndTarget, port: string) {
  return t.kind === "claimed" ? { serial: t.device.serial, port } : { planned_device_id: t.planned.id, port };
}

/** The ports of a target that links already cable, each with the other end
 * in words — through the serial for a device, through the row for a planned
 * device. */
export function takenAt(t: EndTarget, links: Link[], name: (serial: string) => string): Map<string, string> {
  const taken = new Map<string, string>();
  for (const l of links) {
    const e = t.kind === "claimed" ? otherEnd(l, t.device.serial) : otherEndOfPlanned(l, t.planned.id);
    if (e) taken.set(e.mine.port, endWords(e.other, name));
  }
  return taken;
}

/** The site's plan, for the far end of a link: one read, the whole set. */
export function usePlan(siteId: string | undefined) {
  return useQuery(() => siteId
    ? api.GET("/sites/{id}/planner/devices", { params: { path: { id: siteId } } })
    : SKIP, [siteId]);
}

/** The site's declared links: one read, the whole set (bounded by the
 * site's port count, so there is no page). */
export function useLinks(siteId: string | undefined) {
  return useQuery(() => siteId
    ? api.GET("/sites/{id}/links", { params: { path: { id: siteId } } })
    : SKIP, [siteId]);
}

/** A device's name from the site's list and its plan (#1906, `pieces.tsx`),
 * or its serial when neither has it — a link outlives an unclaim, and says so
 * by the serial. */
export { namer };

/** The site's devices, for the names a link's ends carry only as serials,
 * for the picker's list, and for the switches an access point's
 * neighbourhood reads (#371): one read, made by the page once something on
 * it needs the list, through the same per-cell cache the list pays (§9.5).
 * The page makes it and hands it down, so the Links section and the
 * neighbourhood are one consumer of it rather than two (#198). */
//
// **Read to its end** (#1259): every page, so a picker, a link's far end and
// the plan's drawing see the whole site rather than its first 200 serials. The
// shape is `useQuery`'s, so no consumer changed to get it; a later page that
// failed leaves the rows already read and `data.incomplete` set, and a consumer
// that lists or picks devices says so with `IncompleteNote`.
export function useSiteDevices(siteId: string | undefined, category?: "ap" | "switch") {
  return useDrained((page_token?: string) => siteId
    ? api.GET("/sites/{id}/devices", { params: { path: { id: siteId }, query: { page_size: 200, ...(page_token ? { page_token } : {}), ...(category ? { category } : {}) } } })
    : SKIP, [siteId, category]);
}

export default function DeviceLinks({ device, q, devices, selected, mayConfigure, live }: {
  device: Device;
  /** The page's one live read (#514), for what the switch observes on the pressed port. */
  live?: ReturnType<typeof useLiveState>;
  /** The site's links as read; this section shows the ones that touch `device`. */
  q: ReturnType<typeof useLinks>;
  /** The site's devices, the page's read (#371), for the names of the far ends. */
  devices: ReturnType<typeof useSiteDevices>;
  /** The port pressed on the faceplate at the head (#589): what the switch observes there is said here. */
  selected?: string;
  /** `network.write` at the site: the tab then says where a link is
   * withdrawn. It withdraws none itself. */
  mayConfigure: boolean;
}) {
  const links = q.data?.links;
  const mine = (links ?? []).map((l) => ({ link: l, ends: otherEnd(l, device.serial) })).filter((x): x is { link: Link; ends: NonNullable<ReturnType<typeof otherEnd>> } => !!x.ends)
    .sort((a, b) => byNumber(a.ends.mine.port, b.ends.mine.port));
  const cabled = selected ? mine.find((m) => m.ends.mine.port === selected) : undefined;
  const liveSet = live?.status === "ready" ? live.data : undefined;
  // What the switch observes on the pressed port, once the read has answered
  // for it: said in words, since the faceplate's marks are link and power.
  const observed = selected && liveSet && !liveSet.unanswered ? liveSet.ports.find((p) => p.port === selected) : undefined;
  const name = namer(devices.data?.items ?? []);
  return (
    <Card className="panel" title="Links"
      head={(
        // What a declared link is, behind the ⓘ beside the title; the link to Design › Wired stays
        // in the open beside it (#1900).
        <>
          <Info label="About links">
            {/* Restates ADR-0045 — a link is intent for people and an input to nothing that configures a
                device; the topology compares it with what the switch observes and never merges the two. */}
            A declared link says which port is cabled to which. It is a record for people, versioned and audited, and it configures
            nothing. The faceplate above shows what the switch observes, and the site&rsquo;s topology compares the two and never merges them.
            {mayConfigure
              ? <> To declare a link, change its role or withdraw it, go to Design › Wired&rsquo;s topology and press a port on each of two devices, or a line between them.</>
              : <> A link is declared on Design › Wired&rsquo;s topology. Your role cannot declare one at this site.</>}
          </Info>
          {mayConfigure && <ScopeLink to="/design/wired">Design › Wired</ScopeLink>}
        </>
      )}>
      <p className="face-selected" aria-live="polite">
        {!selected && <span className="muted">Select a port on the faceplate above to read what the switch observes on it and which link is declared from it.</span>}
        {selected && live && observed && <>The switch observes on <code>{observed.port}</code>: link <LinkCell status={live.status} port={observed} /> · neighbor <NeighbourCell status={live.status} port={observed} /></>}
        {selected && !(live && observed) && <><code>{selected}</code> pressed</>}
        {selected && (cabled ? <> · declared to {endWords(cabled.ends.other, name)}{cabled.link.role && <> ({cabled.link.role})</>}</> : <> · no link declared from it</>)}
      </p>
      {q.status === "loading" && !links && <Loading label="Reading the site's links" />}
      {q.status === "error" && <ProblemBox problem={q.problem} />}
      {links && (mine.length === 0
        ? <p className="muted">No link is declared at {device.name}.</p>
        : (
          <div className="table-wrap"><table aria-label={`Links at ${device.name}`}>
            <thead><tr><th>This end</th><th>Other end</th><th>Role</th><th>Declared</th></tr></thead>
            <tbody>{mine.map(({ link, ends }) => (
              <tr key={link.id}>
                <td><code>{ends.mine.port}</code></td>
                <td><EndCell end={ends.other} name={name} /></td>
                <td><RoleCell link={link} name={name} /></td>
                <td><When value={link.created_at} /></td>
              </tr>
            ))}</tbody>
          </table></div>
        ))}
    </Card>
  );
}

/** The word a link carries (ADR-0090), in the table: the direction said
 * from end A, or the stack, or nothing. */
export function RoleCell({ link, name }: { link: Link; name(serial: string): string }) {
  if (!link.role) return <span className="muted">—</span>;
  if (link.role === "stack") return <><code>stack</code></>;
  return <><code>uplink</code> <span className="dim">· {endWords(link.a, name)} reaches the WAN through {endWords(link.b, name)}</span></>;
}

/** The far end of a link as the table shows it: the device as a link to its
 * page when it is claimed, a planned device by its planned name, and its
 * port. A planned device bound to a serial is that device, so it is drawn
 * once, as the device (#1906): `name` says it by the bound planned name. */
export function EndCell({ end, name }: { end: Link["a"]; name(serial: string): string }) {
  if (end.kind === "planned" && end.planned_device) {
    const pd = end.planned_device;
    if (pd.serial) return <><DeviceRef serial={pd.serial} name={boundName(pd, name)}>{boundName(pd, name)}</DeviceRef> <code>{end.port}</code></>;
    return <>{pd.name} <span className="tag">planned</span> <code>{end.port}</code></>;
  }
  if (end.kind !== "claimed" || !end.serial) return <>{endWords(end, name)}</>;
  return <><DeviceRef serial={end.serial} name={name(end.serial)}>{name(end.serial)}</DeviceRef> <code>{end.port}</code></>;
}

/** An end's front, from what `/v1` says its ports are: a switch's own
 * configuration document through the live read; an access point's physical
 * ports as its own live report names them (#1124), and its catalogue model's
 * where it has no report; a planned device's catalogue model. None enumerable
 * — a switch with no document on its gateway, an access point with no report
 * of a model the catalogue does not recognise, a planned device whose model
 * has since left the catalogue — is a typed name, with the reason said. */
export function EndFace({ target, selected, taken, onSelect, compact = false, preferUplink = false }: { target: EndTarget; selected?: string; taken: Map<string, string>; onSelect(port: string | undefined): void;
  /** The vector face alone, no picture, and a model's one port pressed for
   * the caller (#622): the shape the topology's popup wants, where the
   * picture was a page's worth of nothing to press. */
  compact?: boolean;
  /** Press an access point's uplink for the caller when nothing is pressed
   * yet (#1124): the popup asks it when the other end is a switch, because
   * an access point reaches a switch by its uplink. The caller may press
   * another port, and a press releases it as any port is released. */
  preferUplink?: boolean;
}) {
  return target.kind === "claimed"
    ? <FarFace device={target.device} selected={selected} taken={taken} onSelect={onSelect} compact={compact} preferUplink={preferUplink} />
    : <PlannedFace planned={target.planned} selected={selected} taken={taken} onSelect={onSelect} compact={compact} />;
}

type FaceProps = { selected?: string; taken: Map<string, string>; onSelect(port: string | undefined): void; compact: boolean };

/** An access point's face from its own report (#1124): every physical port
 * the report names, the uplink first. Named and not placed — the catalogue's
 * layout is for its configuration names, and the uplink is not one of them
 * (ADR-0052 decision 2). */
export function reportedFace(ports: ReportedPort[]): FacePort[] {
  return [...ports].sort((x, y) => Number(y.upstream) - Number(x.upstream) || byNumber(x.port, y.port)).map((p) => ({ name: p.port }));
}

function FarFace({ device, selected, taken, onSelect, compact, preferUplink }: { device: Device; preferUplink: boolean } & FaceProps) {
  const isSwitch = device.category === "switch";
  // Through `documentKeyed` (ADR-0266): a link's end is a port in the
  // switch's configuration's vocabulary, and a report key is not offered as one.
  const live = documentKeyedRead(useQuery(() => isSwitch
    ? api.GET("/switches/{serial}/ports/state", { params: { path: { serial: device.serial } } })
    : SKIP, [device.serial, isSwitch]));
  const { match, matchStatus, model, modelStatus, read } = useDeviceModel(device.serial);
  let ports: FacePort[] | undefined;
  let why: ReactNode;
  let uplink: string | undefined;
  const reported = isSwitch ? undefined : read?.reported_ports;
  if (isSwitch) {
    if (live.status === "ready" && live.data.ports.length > 0) ports = live.data.ports.map((p) => ({ name: p.port }));
    else if (live.status === "ready") why = <>The switch&rsquo;s ports could not be listed: {live.data.unanswered ? reasonWords(live.data.unanswered.reason) : "its configuration declares none"}.</>;
    else if (live.status === "error") why = <>The switch&rsquo;s ports could not be read: {live.problem.message}</>;
  } else if (reported && reported.length > 0) {
    // The vocabulary the write checks an access point's end in (#1124), so
    // what is offered is what will be accepted — the uplink included.
    ports = reportedFace(reported);
    uplink = reported.find((p) => p.upstream)?.port;
  } else if (model && model.ports.length > 0) {
    ports = facePorts(model);
  } else if (matchStatus === "ready" && match?.state !== "recognised") {
    why = <>This access point has not reported its ports and matches no catalog model, so its ports cannot be listed. The API accepts any name here, because nothing can check it.</>;
  } else if (modelStatus === "ready" && model && model.ports.length === 0) {
    why = <><code>{model.model}</code> declares no ports.</>;
  }
  const loading = isSwitch ? live.status === "loading" : matchStatus === "loading" || (!reported && match?.state === "recognised" && modelStatus === "loading");
  // Restates #1124's ruling — an access point's cable end is a physical port its own report
  // names; with no report the catalogue's configuration names are offered and nothing refuses.
  const note = !isSwitch && ports && !reported ? "These ports come from the catalog model. This access point has not reported its own ports, so its uplink is not among them, and the API checks nothing here." : undefined;
  // A face drawn from the report takes no photo: the model's picture boxes its
  // configuration names, and the report's uplink is not one of them.
  return <FaceOrName name={device.name} ports={ports} model={reported?.length ? undefined : model} why={why} note={note} loading={loading} selected={selected} taken={taken} onSelect={onSelect} compact={compact} prefer={preferUplink ? uplink : undefined} />;
}

/** A planned device's front is its catalogue model's and nothing live
 * (ADR-0068 decision 5): the model it was planned as, read once. */
function PlannedFace({ planned, selected, taken, onSelect, compact }: { planned: PlannedDevice } & FaceProps) {
  const model = useQuery(() => planned.model_in_catalogue
    ? api.GET("/device-models/{model}", { params: { path: { model: planned.model } } })
    : SKIP, [planned.model, planned.model_in_catalogue]);
  let ports: FacePort[] | undefined;
  let why: ReactNode;
  if (!planned.model_in_catalogue) why = <><code>{planned.model}</code> is no longer in the catalog, so its ports cannot be listed. The API accepts any name here, because nothing can check it.</>;
  else if (model.status === "ready" && model.data.ports.length > 0) ports = facePorts(model.data);
  else if (model.status === "ready") why = <><code>{planned.model}</code> declares no ports.</>;
  else if (model.status === "error") why = <>The model could not be read: {model.problem.message}</>;
  return <FaceOrName name={planned.name} ports={ports} model={model.data} why={why} loading={model.status === "loading"} selected={selected} taken={taken} onSelect={onSelect} compact={compact} />;
}

function FaceOrName({ name, ports, model, why, note, loading, selected, taken, onSelect, compact = false, prefer }: {
  name: string; ports?: FacePort[]; model?: DeviceModel; why?: ReactNode; note?: string; loading: boolean; selected?: string; taken: Map<string, string>; onSelect(port: string | undefined): void; compact?: boolean;
  /** A port to press for the caller when nothing is pressed yet: an access
   * point's uplink when the other end is a switch (#1124). */
  prefer?: string;
}) {
  const states = Object.fromEntries([...taken].map(([p, to]) => [p, { cabled: to }]));
  // A model with one port has one answer; in the compact face it is pressed
  // for the caller (#622), and a press releases it as any port is released.
  // Otherwise the preferred port, when the face offers it and it is free.
  const only = compact && ports?.length === 1 && !taken.has(ports[0]!.name) ? ports[0]!.name : undefined;
  const preferred = prefer && ports?.some((p) => p.name === prefer) && !taken.has(prefer) ? prefer : undefined;
  const auto = only ?? preferred;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- once per answer: the press is the caller's to release, so `selected` is not a reason to press again
  useEffect(() => { if (auto && selected === undefined) onSelect(auto); }, [auto]);
  return (
    <div className="far-face">
      {loading && <Loading label={`Reading ${name}'s ports`} />}
      {ports && (
        <>
          <div className="face-wrap">
            <DeviceFace ports={ports} states={states} selected={selected} onSelect={(n) => onSelect(selected === n ? undefined : n)}
              label={`${name}, ${ports.length} ${plural(ports.length, "port")}`} photo={compact ? undefined : photoOf(model)} fit />
          </div>
          {note && <p className="muted small">{note}</p>}
          <p className="face-selected" aria-live="polite">
            {selected
              ? (taken.has(selected) ? <><code>{selected}</code> is already cabled to {taken.get(selected)}; a port is cabled once.</> : <>{name} <code>{selected}</code></>)
              : <span className="muted">Press the port at {name}&rsquo;s end.</span>}
          </p>
        </>
      )}
      {!ports && !loading && (
        <>
          {why && <p className="muted">{why}</p>}
          <label className="field">
            <span>Port at {name}</span>
            <input value={selected ?? ""} onChange={(e) => onSelect(e.target.value.trim() || undefined)} pattern="^\S{1,64}$" placeholder="As the device names it" aria-label={`Port at ${name}`} />
          </label>
        </>
      )}
    </div>
  );
}
